import * as RadioGroup from '@radix-ui/react-radio-group';
import type { ReactNode } from 'react';

import './ui.css';

export interface SegmentedOption<T extends string> {
	value: T;
	label: ReactNode;
}

export interface SegmentedProps<T extends string> {
	options: readonly SegmentedOption<T>[];
	value: T;
	onChange: (value: T) => void;
	/** Its name, or point `labelledBy` at a visible label. */
	label?: string | undefined;
	labelledBy?: string | undefined;
	describedBy?: string | undefined;
	className?: string | undefined;
	testId?: string | undefined;
}

/**
 * One choice of a few, side by side (Settings → Theme): the chosen option printed solid
 * (ui.css `.seg`). Built on Radix RadioGroup, so it's a radiogroup with one Tab stop (the
 * chosen option), and the arrow keys move the choice and focus along, wrapping at the ends.
 * Options size to their words, so none truncates. For views that swap the panel under them,
 * use `Tabs` (with the same look) instead.
 */
export function Segmented<T extends string>({ options, value, onChange, label, labelledBy, describedBy, className, testId }: SegmentedProps<T>) {
	return (
		<RadioGroup.Root
			className={className ? `seg ${className}` : 'seg'}
			value={value}
			onValueChange={(next) => {
				if (next && next !== value) onChange(next as T);
			}}
			loop
			aria-label={label}
			aria-labelledby={labelledBy}
			aria-describedby={describedBy}
			data-testid={testId}
		>
			{options.map((o) => (
				<RadioGroup.Item key={o.value} value={o.value} className="seg-opt">
					{o.label}
				</RadioGroup.Item>
			))}
		</RadioGroup.Root>
	);
}
